import { useRef, useState } from 'react';
import useDialogFocus from '../../hooks/useDialogFocus';
import expenseReportsService from '../../services/expenseReportsService';
import { currentReceipts, earlierReceipts, errorMessage, fmtCents } from './helpers';
import ReceiptPicker, { initialReceiptState, receiptChangeFor, receiptProblem } from './ReceiptPicker';

/**
 * View, download, add or remove an expense's receipts. mode 'mine' uses the
 * employee's own routes; 'review' uses the approver's, which also show earlier
 * (removed or replaced) receipts. Paid expenses are read-only.
 */
const ReceiptsModal = ({ expense, mode, onSaved, onClose }) => {
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef);
  const current = currentReceipts(expense);
  const [state, setState] = useState(() => initialReceiptState(expense, current));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const readOnly = expense.status === 'paid';

  const getLink = (receipt, disposition) =>
    mode === 'review'
      ? expenseReportsService.reviewReceiptLink(expense, receipt.receipt_id, disposition)
      : expenseReportsService.myReceiptLink(expense, receipt.receipt_id, disposition);

  const save = async () => {
    const problem = receiptProblem(state, current);
    if (problem) {
      setError(problem);
      return;
    }
    const change = receiptChangeFor(state, current, expense);
    if (!change) {
      onClose();
      return;
    }
    try {
      setSaving(true);
      setError('');
      const updated = mode === 'review'
        ? await expenseReportsService.changeReceiptsAsApprover(expense, change)
        : await expenseReportsService.changeMyReceipts(expense.expense_id, change);
      onSaved(updated);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const uploading = state.uploads.some((u) => u.status === 'uploading');

  return (
    <div
      ref={dialogRef}
      className="modal show d-block"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="receiptsTitle"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="receiptsTitle">
              <i className="bi bi-paperclip me-2"></i>
              Receipts
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <p className="text-muted small">
              {expense.employee_name} · {expense.merchant} · {fmtCents(expense.amount_cents)}
              {readOnly && ' · Paid, so receipts can no longer change.'}
            </p>
            <ReceiptPicker
              current={current}
              earlier={mode === 'review' ? earlierReceipts(expense) : []}
              state={state}
              setState={setState}
              getLink={getLink}
              readOnly={readOnly}
              idPrefix="receiptsModal"
            />
            {error && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {error}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving || uploading}>
                {saving ? 'Saving…' : 'Save receipts'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptsModal;
